import React, { useState } from "react";

import { useGetFarms, useLocation, useNavigate, useTranslation, useUserContext } from "utils";

import { DeleteFarmModal, Drawer } from "components";

import { FarmSwitch, FarmSwitchFarm } from "pages/app/_app/components/farm-switch/FarmSwitch";
import { HOME_ROUTE, getRouteAfterFarmSwitch, isFarmSwitchHiddenOn } from "pages/app/_app/components/farm-switch/farmSwitchRules";
import { useSwitchFromDeletedFarm } from "pages/app/_app/components/farm-switch/useSwitchFromDeletedFarm";
import { FarmDetails } from "pages/app/farm/components/FarmDetails";

type GlobalFarmSwitchProps = {
  /** Open tasks per farm id; nothing supplies them yet, so the indicator stays hidden. */
  openTaskCounts?: Record<string, number>;
};

export const GlobalFarmSwitch = ({ openTaskCounts }: GlobalFarmSwitchProps) => {
  const { t } = useTranslation("onboarding");
  const location = useLocation();
  const navigate = useNavigate();
  const { data: farms, currentFarm, isSuccess: farmsLoaded, refetch: refetchFarms } = useGetFarms();
  const { user, switchFarm, reloadUserData } = useUserContext();
  const switchFromDeletedFarm = useSwitchFromDeletedFarm(currentFarm?.id);
  const [addingFarm, setAddingFarm] = useState(false);
  const [editFarmId, setEditFarmId] = useState<string | null>(null);
  const [deleteFarmId, setDeleteFarmId] = useState<string | null>(null);

  // the user already names the current farm, so the trigger is in place before the list arrives and stays if it never does;
  // a list that loaded empty means there is no farm, whatever the user object still says
  const knownFarms: FarmSwitchFarm[] = user?.farm?.id && user.farm.name ? [{ id: user.farm.id, name: user.farm.name }] : [];
  const shownFarms: FarmSwitchFarm[] = farms.length
    ? farms.map((farm) => ({ id: farm.id, name: farm.name, openTaskCount: openTaskCounts?.[farm.id] }))
    : farmsLoaded
    ? []
    : knownFarms;

  if (!shownFarms.length || isFarmSwitchHiddenOn(location.pathname)) return null;

  const goHome = () => {
    if (location.pathname !== HOME_ROUTE) navigate(HOME_ROUTE);
  };

  const handleSelect = (farmId: string) => {
    if (farmId === currentFarm?.id) return;

    switchFarm(farmId);
    reloadUserData();

    const next = getRouteAfterFarmSwitch(
      location.pathname,
      farms.find((farm) => farm.id === farmId)
    );
    if (next) navigate(next);
  };

  const handleDeleted = (farmId: string) => {
    if (switchFromDeletedFarm(farmId)) goHome();
  };

  const farmToDelete = farms.find((farm) => farm.id === deleteFarmId);

  return (
    <>
      <FarmSwitch
        farms={shownFarms}
        activeFarmId={currentFarm?.id ?? user?.farm?.id ?? undefined}
        disabled={!farms.length}
        onSelect={handleSelect}
        onEdit={setEditFarmId}
        onDelete={setDeleteFarmId}
        onAdd={() => setAddingFarm(true)}
      />
      <Drawer dataTestId="farm-switch-add-farm-drawer" title={t("onboardingFormHeader.farm")} visible={addingFarm} onClose={() => setAddingFarm(false)}>
        <FarmDetails
          isOnboarding={false}
          selectedFarmId=""
          handleBack={() => setAddingFarm(false)}
          onFinish={() => {
            setAddingFarm(false);
            refetchFarms();
          }}
        />
      </Drawer>
      {editFarmId ? (
        <Drawer dataTestId="farm-switch-edit-farm-drawer" title={t("onboardingFormHeader.editFarm")} visible onClose={() => setEditFarmId(null)}>
          <FarmDetails
            isOnboarding={false}
            selectedFarmId={editFarmId}
            handleBack={() => setEditFarmId(null)}
            onFinish={() => {
              setEditFarmId(null);
              refetchFarms();
              reloadUserData();
            }}
          />
        </Drawer>
      ) : null}
      <DeleteFarmModal
        farm={farmToDelete ? { id: farmToDelete.id, name: farmToDelete.name } : null}
        onClose={() => setDeleteFarmId(null)}
        onDeleted={handleDeleted}
      />
    </>
  );
};
